import React from 'react';
import clsx from 'clsx';
import { DivPropsWithoutRef } from '../../common/types';
import { Icon } from '../Icon';
import { renderSlot, Slot } from '../../common/renderSlot';

export interface BaseAlertSlotProps {
  /**
   * Вызывайте, чтобы закрыть тост или алерт
   */
  close?: () => void;
}

export interface BaseAlertProps
  extends Omit<DivPropsWithoutRef, 'icon' | 'title'> {
  /**
   * Слот для заголовка.
   *
   * Предоставляет пропы:
   * - `close?: () => void` - Вызывайте, чтобы закрыть тост или алерт
   */
  title?: Slot<BaseAlertSlotProps>;
  /**
   * Слот для контента.
   *
   * Предоставляет пропы:
   * - `close?: () => void` - Вызывайте, чтобы закрыть тост или алерт
   */
  description?: Slot<BaseAlertSlotProps>;
  /**
   * Иконка
   * @type React.ElementType
   */
  icon?: React.ElementType;
  /**
   * Слот для действий.
   *
   * Предоставляет пропы:
   * - `close?: () => void` - Вызывайте, чтобы закрыть тост или алерт
   */
  actions?: Slot<BaseAlertSlotProps>;
  /**
   * Слот для кнопки закрытия
   */
  closeButton?: React.ReactNode;
  /**
   * Функция закрытия алерта или тоста
   */
  close?: () => void;
  /**
   * Дополнительные css-классы элементов
   */
  classes?: {
    wrapper?: string;
    icon?: string;
    content?: string;
    text?: string;
    title?: string;
    description?: string;
    actions?: string;
  };
}

export function BaseAlert({
  close,
  title,
  description,
  icon,
  className,
  actions,
  closeButton,
  classes,
  ...props
}: BaseAlertProps) {
  return (
    <div
      className={clsx(
        'hse-BaseAlert',
        {
          'hse-BaseAlert_hasDescription': Boolean(description)
        },
        className
      )}
      {...props}
    >
      <div className={clsx('hse-BaseAlert__wrapper', classes?.wrapper)}>
        <Icon
          className={clsx('hse-BaseAlert__icon', classes?.icon)}
          icon={icon}
          size={Icon.Size.medium}
        />
        <div className={clsx('hse-BaseAlert__content', classes?.content)}>
          <div className={clsx('hse-BaseAlert__text', classes?.text)}>
            <div className={clsx('hse-BaseAlert__title', classes?.title)}>
              {renderSlot(title, { close })}
            </div>
            {description ? (
              <div
                className={clsx(
                  'hse-BaseAlert__description',
                  classes?.description
                )}
              >
                {renderSlot(description, { close })}
              </div>
            ) : null}
          </div>
          {actions ? (
            <div className={clsx('hse-BaseAlert__actions', classes?.actions)}>
              {renderSlot(actions, {
                close
              })}
            </div>
          ) : null}
        </div>
        {closeButton}
      </div>
    </div>
  );
}
